import { IcChevronBold } from '@assets/icons/24x24';
import { ButtonGroup, Icon, Pagination as ChakraPagination, Text } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';

interface PageTextProps {
  page: number;
  size: number;
  total: number;
}

const PageText = ({ page, size, total }: Readonly<PageTextProps>) => {
  const pageFirstItem = page * size + 1;
  const pageLastItem = Math.min((page + 1) * size, total);

  return (
    <Text color="neutral.300" lineHeight="125%" flex="1">
      <FormattedMessage id="global.pagination.pageText" values={{ pageFirstItem, pageLastItem, total }} />
    </Text>
  );
};

interface PaginationProps {
  currentPage: number;
  pageSize: number;
  total: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export function Pagination({ currentPage, pageSize, total, onPreviousPage, onNextPage }: Readonly<PaginationProps>) {
  const totalPages = Math.ceil(total / pageSize);
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;

  const shouldPaginate = total > pageSize;

  if (!shouldPaginate) {
    return (
      <Text color="neutral.300" lineHeight="125%" w="full">
        <FormattedMessage id="global.pagination.pageText" values={{ pageFirstItem: 1, pageLastItem: total, total }} />
      </Text>
    );
  }

  return (
    <ChakraPagination.Root pageSize={30} defaultPage={1} maxW="240px">
      <ButtonGroup size="sm" w="full" gap={2}>
        <PageText page={currentPage} size={pageSize} total={total} />
        <ChakraPagination.PrevTrigger disabled={isFirstPage}>
          <Icon
            as={IcChevronBold}
            color={isFirstPage ? 'neutral.600' : 'neutral.300'}
            cursor={isFirstPage ? 'default' : 'pointer'}
            onClick={onPreviousPage}
            {...(!isFirstPage && {
              _hover: { color: 'skyBlue' },
            })}
          />
        </ChakraPagination.PrevTrigger>
        <ChakraPagination.NextTrigger disabled={isLastPage}>
          <Icon
            as={IcChevronBold}
            color={isLastPage ? 'neutral.600' : 'neutral.300'}
            cursor={isLastPage ? 'default' : 'pointer'}
            {...(!isLastPage && {
              _hover: { color: 'skyBlue' },
            })}
            onClick={onNextPage}
            style={{ transform: 'rotate(180deg)' }}
          />
        </ChakraPagination.NextTrigger>
      </ButtonGroup>
    </ChakraPagination.Root>
  );
}
